import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Building2, ArrowUpRight, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const culture = [
  { title: "Remote First", desc: "Work from anywhere. Our team is distributed globally." },
  { title: "Technical Excellence", desc: "We value craftsmanship and continuous learning." },
  { title: "Meaningful Impact", desc: "Every team member contributes directly to our products." }
];

export default function CareersPage() {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [applicationData, setApplicationData] = useState({
    name: "",
    email: "",
    phone: "",
    cover_letter: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/careers`);
        setPositions(res.data);
      } catch (error) {
        console.error("Error fetching positions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPositions();
  }, []);

  const handleApplicationChange = (e) => {
    setApplicationData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!selectedPosition) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/careers/apply`, {
        ...applicationData,
        position_id: selectedPosition.id
      });
      toast.success("Application submitted successfully!");
      setApplicationOpen(false);
      setApplicationData({ name: "", email: "", phone: "", cover_letter: "" });
      setSelectedPosition(null);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="careers-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="careers-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1607418554403-1d145ee448cb?auto=format&fit=crop&w=2000&q=80"
            alt="Team collaboration"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/95 to-obsidian" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-6 opacity-0 animate-fade-up">
              Careers
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Join our
              <br />
              <span className="text-mint">team.</span>
            </h1>
            <p className="mt-10 text-xl text-slate-400 leading-relaxed opacity-0 animate-fade-up animate-delay-200">
              We're building software that powers critical business operations. If you're interested in challenging technical problems, explore our open positions.
            </p>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-16 md:py-24 bg-obsidian-light" data-testid="careers-culture">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {culture.map((item, idx) => (
              <div key={item.title} className="p-8 bg-white/5 border border-white/5">
                <span className="text-4xl font-heading font-bold text-white/10">0{idx + 1}</span>
                <h3 className="mt-4 text-xl font-heading font-bold text-white">{item.title}</h3>
                <p className="mt-3 text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="careers-positions">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight mb-16">
            Open Positions
          </h2>

          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white/5 border border-white/5 p-10">
                  <Skeleton className="h-8 w-1/3 bg-white/10 mb-4" />
                  <Skeleton className="h-4 w-1/4 bg-white/10 mb-6" />
                  <Skeleton className="h-20 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : positions.length === 0 ? (
            <div className="text-center py-20 bg-white/5 border border-white/5">
              <p className="text-slate-400 text-xl mb-4">No open positions at this time.</p>
              <p className="text-slate-500">Check back later or send us a general inquiry.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {positions.map((position) => (
                <div
                  key={position.id}
                  className="card-beam p-10"
                  data-testid={`position-card-${position.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
                    <div className="flex-1">
                      <h3 className="text-2xl font-heading font-bold text-white mb-4">
                        {position.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                        <span className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {position.department}
                        </span>
                        <span className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {position.location}
                        </span>
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {position.type}
                        </span>
                      </div>
                      <p className="text-slate-400 leading-relaxed">
                        {position.description}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Dialog open={applicationOpen && selectedPosition?.id === position.id} onOpenChange={(open) => {
                        setApplicationOpen(open);
                        if (open) setSelectedPosition(position);
                      }}>
                        <DialogTrigger asChild>
                          <button 
                            className="btn-primary group flex items-center gap-3 bg-mint text-black px-6 py-4 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors"
                            data-testid={`apply-btn-${position.id}`}
                          >
                            Apply Now
                            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                          </button>
                        </DialogTrigger>
                        <DialogContent className="bg-obsidian-light border-white/10 text-white max-w-lg rounded-none">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-heading font-bold">
                              Apply for {position.title}
                            </DialogTitle>
                            <DialogDescription className="text-slate-400">
                              Submit your application for this position.
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleApply} className="space-y-6 mt-6" data-testid="application-form">
                            <div className="space-y-2">
                              <Label htmlFor="app-name" className="text-slate-300">Name</Label>
                              <Input
                                id="app-name"
                                name="name"
                                value={applicationData.name}
                                onChange={handleApplicationChange}
                                required
                                className="bg-white/5 border-white/10 text-white rounded-none h-12"
                                data-testid="application-input-name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="app-email" className="text-slate-300">Email</Label>
                              <Input
                                id="app-email"
                                name="email"
                                type="email"
                                value={applicationData.email}
                                onChange={handleApplicationChange}
                                required
                                className="bg-white/5 border-white/10 text-white rounded-none h-12"
                                data-testid="application-input-email"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="app-phone" className="text-slate-300">Phone</Label>
                              <Input
                                id="app-phone"
                                name="phone"
                                type="tel"
                                value={applicationData.phone}
                                onChange={handleApplicationChange}
                                required
                                className="bg-white/5 border-white/10 text-white rounded-none h-12"
                                data-testid="application-input-phone"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="app-cover" className="text-slate-300">Cover Letter</Label>
                              <Textarea
                                id="app-cover"
                                name="cover_letter"
                                value={applicationData.cover_letter}
                                onChange={handleApplicationChange}
                                required
                                rows={4}
                                className="bg-white/5 border-white/10 text-white rounded-none resize-none"
                                placeholder="Tell us why you're interested..."
                                data-testid="application-input-cover"
                              />
                            </div>
                            <button
                              type="submit"
                              disabled={submitting}
                              className="w-full bg-mint text-black py-4 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors disabled:opacity-50"
                              data-testid="application-submit-btn"
                            >
                              {submitting ? (
                                <span className="flex items-center justify-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Submitting...
                                </span>
                              ) : (
                                "Submit Application"
                              )}
                            </button>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>

                  {/* Requirements & Benefits */}
                  <div className="mt-10 pt-8 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-4">Requirements</p>
                      <ul className="space-y-3">
                        {position.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-slate-300 text-sm">
                            <span className="w-1.5 h-1.5 mt-2 bg-mint flex-shrink-0" />
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-4">Benefits</p>
                      <ul className="space-y-3">
                        {position.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-slate-300 text-sm">
                            <span className="w-1.5 h-1.5 mt-2 bg-coral flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* General Inquiry CTA */}
      <section className="py-24 md:py-32 bg-obsidian-light" data-testid="careers-cta">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
            Don't see a fit?
          </h2>
          <p className="mt-6 text-xl text-slate-400">
            We're always interested in hearing from talented individuals.
          </p>
          <Link to="/contact">
            <button 
              data-testid="careers-general-inquiry-btn"
              className="mt-10 btn-primary group flex items-center gap-3 bg-mint text-black px-8 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors mx-auto"
            >
              Contact Us
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
